import cn from 'classnames';
import { useRouter } from 'next/router';
import { MouseEventHandler } from 'react';

import { formatEmail } from 'src/utils/formatEmail';
import { formatName } from 'src/utils/formatName';
import { formatPhone } from 'src/utils/formatPhone';

import { ImageData, LinkData } from 'src/types/components';

import Image from '../Image';
import Link from '../Link';
import RichText from '../RichText';
import Text from '../Text';

import styles from './PeopleCard.module.scss';

export type PeopleCardProps = {
  id?: number | string;
  image?: ImageData;
  firstName: string;
  middleName: string;
  lastName: string;
  title: string;
  office: LinkData;
  email: string;
  phone: string;
  url: string;
  className?: string;
  excerpt?: string;
};

const PeopleCard = (props: PeopleCardProps): JSX.Element => {
  const {
    image,
    firstName,
    middleName,
    lastName,
    title,
    office,
    email,
    phone,
    url,
    className,
    excerpt,
  } = props;

  const formattedEmail = formatEmail(email);
  const formattedPhone = formatPhone(phone);

  const router = useRouter();

  const handleClick: MouseEventHandler<HTMLDivElement> = (event) => {
    if (event.target && event.target instanceof Element) {
      // check to see if target or an ancestor of the target is an anchor tag
      const anchorTag = event.target.closest<HTMLAnchorElement>('a');
      if (!anchorTag) {
        router.push(url);
      }
    }
  };

  return (
    <div className={cn(styles.main, className)} onClick={handleClick}>
      <div className={styles.wrapper}>
        {image && (
          <div className={styles['image-wrapper']}>
            <Image className={styles.image} {...image} fill />
          </div>
        )}
        <div className={styles.content}>
          <Link href={url} className={cn(styles.name, 'heading-h4')}>
            {formatName({ firstName, middleName, lastName })}
          </Link>
          <div className={styles.info}>
            <Text className={cn(styles.title, 'body-sm')} field={title} />
            {office.href && (
              <Link href={office.href} className={cn(styles.office, 'body-sm')}>
                {office.label}
              </Link>
            )}
          </div>
          {formattedEmail && (
            <Link
              href={formattedEmail.href}
              className={cn(styles.email, 'body-sm')}
              data-firstname={firstName}
              data-lastname={lastName}
            >
              <span
                className={styles['email-html']}
                dangerouslySetInnerHTML={{ __html: formattedEmail.emailHtml }}
              />
            </Link>
          )}
          {formattedPhone && (
            <Link href={formattedPhone.href} className={cn(styles.phone, 'body-sm')}>
              {formattedPhone.text}
            </Link>
          )}
        </div>
      </div>
      <RichText
        className={cn(styles.excerpt, 'body-sm')}
        field={excerpt}
        noDefaultBodyStyle={true}
      />
    </div>
  );
};

export default PeopleCard;
